import { Button, Icon, IconButton, Badge, tokens } from '@hotjar/hotsauce';
import type { IconName } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

export interface ListButtonProps {
  text: string;
  path?: string;
  onClick?: () => void;
  icon: IconName;
  isActive?: boolean;
  notificationLabel?: string;
  id?: string;
  isCollapsed: boolean;
  isDisabled?: boolean;
  isExternal?: boolean;
}

const StyledBadge = styled(Badge)``; // workaround as Badge directly has an issue with composition

const listButtonStyles = (isActive: boolean, hasBadge: boolean, showAsDisabled: boolean) => css`
  position: relative;
  border-radius: ${tokens.radius.l};
  padding: 0 ${tokens.spacer.x1pt5};
  ${showAsDisabled && `color: ${tokens.color.text_disabled};`}
  ${tokens.text('body2')};

  ${!showAsDisabled
    ? `
    &:active,
    &[aria-current='page'],
    &[aria-current='page']:hover,
    &:focus,
    &:focus:hover {
      ${tokens.text('subtitle2')};
      ${tokens.elevation(0)};
      color: ${tokens.color.primary_cta};
      background-color: ${tokens.color.bg_active};
    }
  
    &:focus-visible {
      ${tokens.focus()};
    }
    
    &:hover {
      background-color: ${tokens.color.bg_hover};
      color: ${tokens.color.text_default};
      ${
        hasBadge &&
        `
        ${StyledBadge} {
          color: ${tokens.color.text_light};
          background-color: ${tokens.color.bg_hover};
        }
      `
      }
    }
  `
    : `
    &:hover {
      background-color: transparent;
      color: ${tokens.color.text_disabled};
    }
    
    &:focus {
      background-color: transparent;
      color: ${tokens.color.text_disabled};
      box-shadow: none;
    }
  `}

  ${hasBadge &&
  `
    ${StyledBadge} {
        ${tokens.transition('all')};
        position: absolute;
        top: calc(50% - ${tokens.spacer.x1});
        right: ${tokens.spacer.x2};
        color: ${tokens.color.primary_cta};
        background-color: ${tokens.color.bg_active};
      }
  `}

  ${isActive &&
  hasBadge &&
  `
    ${StyledBadge} {
      color: ${tokens.color.white};
      background-color: ${tokens.BRAND_COLORS.cobalt_700};
    }
  `}
`;

const OpenExternalLinkIcon = styled(Icon)`
  position: absolute;
  right: ${tokens.spacer.x1};
`;

const StyledButton = styled(Button)<{ isActive: boolean; showAsDisabled: boolean }>`
  width: 100%;
  ${({ isActive, showAsDisabled }) => listButtonStyles(isActive, true, showAsDisabled)}
`;

const Container = styled.div`
  position: relative;
`;

const Dot = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${tokens.BRAND_COLORS.cobalt_400};
  border: 1px solid ${tokens.BRAND_COLORS.cobalt_600};
`;

// missing a / button behaviour for IconButton
export const ListButton = ({
  isCollapsed,
  id,
  text,
  path,
  icon,
  onClick,
  notificationLabel,
  isActive = false,
  isDisabled = false,
  isExternal = false,
  ...buttonProps
}: ListButtonProps): JSX.Element => {
  if (isCollapsed) {
    return (
      <Container>
        <IconButton
          kind="ghost"
          id={id}
          icon={icon}
          as={path ? 'a' : undefined}
          tooltipPlacement="right"
          buttonStyles={listButtonStyles(isActive, false, isDisabled)}
          aria-current={isActive ? 'page' : 'false'}
          href={path}
          description={text}
          tooltipText={
            (notificationLabel ? (
              <>
                {text}
                <Badge
                  kind="custom"
                  size="s"
                  color={tokens.BRAND_COLORS.neutral_600}
                  textColor={tokens.color.white}
                  css={css`
                    margin-left: ${tokens.spacer.x1};
                  `}
                >
                  {notificationLabel}
                </Badge>
              </>
            ) : (
              text
            )) as string // temporary until we find a way to use badge in hotsauce tooltip
          }
          onClick={() => !isDisabled && onClick?.()}
          isExternal={isExternal}
          {...buttonProps}
        />
        {notificationLabel && <Dot />}
      </Container>
    );
  }
  return (
    <StyledButton
      kind="ghost"
      id={id}
      icon={icon}
      as={path ? 'a' : undefined}
      onClick={() => !isDisabled && onClick?.()}
      href={path}
      isActive={isActive}
      aria-current={isActive ? 'page' : 'false'}
      css={css`
        > svg {
          margin-right: ${tokens.spacer.x2};
        }
      `}
      showAsDisabled={isDisabled}
      isExternal={isExternal}
      {...buttonProps}
    >
      {
        (
          <>
            {text}
            {notificationLabel && (
              <StyledBadge kind="custom" size="s">
                {notificationLabel}
              </StyledBadge>
            )}
            {isExternal && <OpenExternalLinkIcon icon="open_new_window" size="m" />}
          </>
        ) as unknown as string // temporary until we find a way to use badge in hotsauce button
      }
    </StyledButton>
  );
};
